import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { IntegrityEdit } from 'src/app/integrity/model/integrity-edit';
import { MessengerService } from './messenger.service';
import { SettingService } from './setting.service';
import { SessionSetting } from '../model/session-setting';

@Injectable({
  providedIn: 'root'
})
export class IntegrityService implements OnDestroy
{
  public integrityTypes = [
    {
      key: 'INTEGRITY-BATCH-TYPE-ADDRESS-MEMBER', value: 'ADDRESS_MEMBER', label: 'Address Validation for Member',
    },
    /*Disable Care Team in V1
    {
      key: 'INTEGRITY-BATCH-TYPE-ADDRESS-CARE-TEAM', value: 'ADDRESS_CARE_TEAM', label: 'Address Validation for Care Team',
    },*/
  ];

  public integrityStages = [
    {
      key: 'INTEGRITY-BATCH-STATE-IN-REVIEW', value: 'IN_REVIEW', label: 'In Review', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INTEGRITY-BATCH-STATE-VALIDATING', value: 'VALIDATING', label: 'Performing Validations', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INTEGRITY-BATCH-STATE-VALIDATED', value: 'VALIDATED', label: 'Completed Validations', icon: 'check',
      disabled: true, isValid: true
    },
    {
      key: 'INTEGRITY-BATCH-STATE-READY-TO-PROCESS', value: 'READY_TO_PROCESS', label: 'Ready to Process', icon: 'check',
      disabled: true, isValid: true
    },
    {
      key: 'INTEGRITY-BATCH-STATE-PROCESSING', value: 'PROCESSING', label: 'Processing', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INTEGRITY-BATCH-STATE-COMPLETED', value: 'COMPLETED', label: 'Completed', icon: 'check',
      disabled: true, isValid: true
    },
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private settingService: SettingService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get an Integrty Batch by id.
   *
   * @param id Integrity Batch id
   */
  public getIntegrityBatchById(id: number): Observable<IntegrityEdit>
  {
    return this.http.get<IntegrityEdit>(`/integrity/batch/${id}`);
  }

  /**
   * Delete an Integrity Batch by id.
   *
   * @param id Integrity Batch id
   */
  public deleteIntegrityBatch(id: number): Observable<IntegrityEdit>
  {
    return this.http.delete<IntegrityEdit>(`/integrity/batch/${id}`);
  }

  /**
   * Process an Integrtity Batch by id.
   *
   * @param id Integrity Batch id
   */
  public processIntegrityBatch(id: number): Observable<IntegrityEdit>
  {
    return this.http.post<IntegrityEdit>(`/integrity/process/batch/${id}`, null);
  }

  /**
   * Get the integrity stage description to use
   *
   * @param integrityStage Integrity stage
   * @returns string Integrity stage description
   */
  public getIntegrityStageText(integrityStage: string): string
  {
    let text = integrityStage;
    const aValue = this.integrityStages.find(x => x.value === integrityStage);
    if (aValue !== undefined)
    {
      text = aValue.label;
    }
    return text;
  }

  /**
   * Get the integrity type description to use
   *
   * @param integrityStage Integrity type
   * @returns string Integrity type description
   */
  public getIntegrityTypeText(integrityType: string): string
  {
    let text = integrityType;
    const aValue = this.integrityTypes.find(x => x.value === integrityType);
    if (aValue !== undefined)
    {
      text = aValue.label;
    }
    return text;
  }

  /**
   * Save a Integrity Batch.
   *
   * @param data Integrity batch to persit
   * @param isNew Indicate if this is a new or update
   */
  public saveIntegrityBatch(data: any, isNew: boolean, id?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/integrity/batch', data);
    }
    else
    {
      return this.http.put(`/integrity/batch/${id}`, data);
    }
  }

  /**
   * Update integrity batch item.
   */
  public updateIntegrityBatchItemAddress(data: any): Observable<any>
  {
    return this.http.put('/integrity/batch/item/address/update', data);
  }

  /**
   * Toggle integrity batch items.
   */
  public updateIntegrityBatchItemsAddress(data: any[]): Observable<any>
  {
    return this.http.put('/integrity/batch/items/address/update/discard', data);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    const stagesTranslationKeys = this.integrityStages.map(a => a.key);
    this.translate.get(stagesTranslationKeys).subscribe({
      next: (res: string) =>
      {
        for (let i = 0; i < this.integrityStages.length; i++)
        {
          const key = 'INTEGRITY-BATCH-STATE-' + this.integrityStages[i].value.replace(/_/g, '-');
          this.integrityStages[i].label = res[key];
        }
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        const integrityTypesTranslationKeys = this.integrityTypes.map(a => a.key);
        this.translate.get(integrityTypesTranslationKeys).subscribe({
          next: (res: string) =>
          {
            for (let i = 0; i < this.integrityTypes.length; i++)
            {
              const key = 'INTEGRITY-BATCH-TYPE-' + this.integrityTypes[i].value.replace(/_/g, '-');
              this.integrityTypes[i].label = res[key].replace('{{patientLabel}}', sessionSetting.patientLabel);
            }
          }
        });
      }
    });
  }
}
